import React, { useEffect, useState } from 'react';
import { Switch } from '@mui/material';
import { PeopleAlt } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { DynamicContainer } from './common/DynamicContainer';
import { fetchMfaStatus, setMfaStatus } from '../services/mfaApi';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/users/global`;

const GlobalUsers = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);

  const isCdxUser = () => {
    try {
      return localStorage.getItem('cdx_user') === 'true';
    } catch {
      return false;
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const mapped = (Array.isArray(data) ? data : []).map((item, idx) => ({
        ...item,
        id: `${item.tenantId ?? 't'}-${item.globalUserId ?? idx}-${item.username ?? idx}`,
        cdxUserDisplay: item.cdxUser ? 'Yes' : 'No',
        roleName: item.roleName || '—',
        tenantName: item.tenantName || '—',
        email: item.email || item.username || '—',
        name: item.name || '—',
        mfaEnabled: false,
      }));

      const withMfa = await Promise.all(
        mapped.map(async (row) => {
          if (row.globalUserId == null) return row;
          try {
            const mfa = await fetchMfaStatus(row.globalUserId);
            return { ...row, mfaEnabled: !!mfa.mfaEnabled };
          } catch {
            return row;
          }
        })
      );
      setUsers(withMfa);
    } catch (error) {
      console.error('Global users fetch failed:', error);
      toast.error(`Error fetching global users: ${error.message}`);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGridMfaToggle = async (row, enabled) => {
    if (row?.globalUserId == null) {
      toast.error('Cannot update MFA: global user id missing');
      return;
    }
    try {
      await setMfaStatus(row.globalUserId, enabled);
      setUsers((prev) =>
        prev.map((u) => (u.id === row.id ? { ...u, mfaEnabled: enabled } : u))
      );
      toast.success(
        enabled
          ? `MFA enabled for ${row.email || row.username}`
          : `MFA disabled for ${row.email || row.username}`
      );
    } catch (e) {
      toast.error(e.message || 'Failed to update MFA');
    }
  };

  const columns = [
    { field: 'tenantName', headerName: 'Tenant', width: 180 },
    { field: 'name', headerName: 'Name', width: 180 },
    { field: 'email', headerName: 'Email', width: 220 },
    { field: 'username', headerName: 'Username', width: 200 },
    { field: 'roleName', headerName: 'Role', width: 180 },
    { field: 'cdxUserDisplay', headerName: 'CDX user', width: 100 },
    {
      field: 'mfaEnabled',
      headerName: 'Login MFA',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Switch
          size="small"
          checked={!!params.row.mfaEnabled}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => handleGridMfaToggle(params.row, e.target.checked)}
          disabled={params.row.globalUserId == null || !isCdxUser()}
        />
      ),
    },
    { field: 'status', headerName: 'Status', width: 90 },
  ];

  return (
    <DynamicContainer
      title="Global Users"
      icon={PeopleAlt}
      columns={columns}
      formFields={[]}
      initialFormData={{}}
      idField="id"
      rows={users}
      isLoading={isLoading}
      isCdxUser={isCdxUser()}
      hideAdd
      hideEdit
      hideDelete
    />
  );
};

export default GlobalUsers;
